import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { CopyCommand } from './Ui';

/** The address people type into the `--server` option: wherever this site is being served from. */
export const serverUrl = (): string => window.location.origin;

/**
 * What the Library holds and when. It is said once per page, where a person decides to share something
 * (creating a repository, or being shown how to push), and it matches what `athenaeum sync` actually sends:
 * accepted wiki Markdown and the minimal repository identity file, and nothing else.
 */
export function TextOnlyNotice() {
  return (
    <p className="hint text-only" style={{ margin: 0 }}>
      <strong>Accepted wiki text only.</strong> The Library stores Markdown pages under <code>wiki/</code> and
      the minimal wiki identity in <code>athenaeum.json</code>. Raw sources, root vault notes, databases,
      search indexes, embeddings, agent sessions and credentials stay on your computer. Sharing is always an
      explicit push: nothing is uploaded until you run <code>athenaeum sync push</code>, and{' '}
      <code>athenaeum sync status</code> shows what would go.
    </p>
  );
}

/**
 * The repository's permanent ID. Unlike `owner/name` it survives a rename, so it can be used anywhere the
 * name can: to link a vault or to clone.
 */
export function RepositoryId({ id }: { id: string }) {
  return (
    <div className="stack">
      <span className="muted">Wiki ID</span>
      <CopyCommand label="Wiki ID" text={id} />
      <span className="hint">
        It never changes, even if the wiki is renamed, and can be used in place of the name.
      </span>
    </div>
  );
}

function Step({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="stack">
      <p className="muted" style={{ margin: 0 }}>
        {title}
      </p>
      {children}
    </div>
  );
}

/**
 * How to work with one existing repository from the terminal. `canWrite` hides the push half from people
 * who can only read it, so a visitor is not shown steps that would be refused.
 */
export function RepoSyncSteps({
  repo,
  server,
  canWrite,
}: {
  repo: { id: string; fullName: string; name: string; head: string | null };
  server: string;
  canWrite: boolean;
}) {
  return (
    <div className="stack sync-steps" data-testid="repo-sync-steps">
      <Step title="Once per computer, sign in. This opens a browser page where you approve the device:">
        <CopyCommand label="Sign in" text={`athenaeum library login --server ${server}`} />
      </Step>
      {repo.head && (
        <Step title="On a computer that has no copy yet, download one into a new folder:">
          <CopyCommand
            label="Clone"
            text={`athenaeum sync clone ${repo.fullName} ./${repo.name} --server ${server}`}
          />
        </Step>
      )}
      {canWrite && (
        <Step title="To connect a vault you already have, run this inside its folder, then push:">
          <CopyCommand
            label="Link"
            text={`athenaeum sync link --server ${server} --remote ${repo.fullName}`}
          />
          <CopyCommand label="Push" text={'athenaeum sync push -m "Describe your change"'} />
        </Step>
      )}
      <Step title="Bring in other people’s changes, and check where you stand:">
        <CopyCommand label="Pull" text="athenaeum sync pull" />
        <CopyCommand label="Status" text="athenaeum sync status" />
      </Step>
      <p className="hint" style={{ margin: 0 }}>
        Scripts and CI can use an access token instead: create one in{' '}
        <Link to="/settings/tokens">Access token settings</Link> and export it as{' '}
        <code>ATHENAEUM_LIBRARY_TOKEN</code>.
      </p>
      <TextOnlyNotice />
    </div>
  );
}

/**
 * Starting from a vault that already exists on the computer: the repository is made from the terminal, so the
 * vault's own repository ID is offered to the server and the two agree from the first push.
 */
export function StartFromVaultSteps({ server, name }: { server: string; name?: string }) {
  const repo = name?.trim() || '<name>';
  return (
    <div className="stack sync-steps" data-testid="start-from-vault">
      <CopyCommand label="Sign in" text={`athenaeum library login --server ${server}`} />
      <CopyCommand label="Create and link" text={`athenaeum sync link --server ${server} --create ${repo}`} />
      <CopyCommand label="First push" text={'athenaeum sync push -m "First push"'} />
      <p className="hint" style={{ margin: 0 }}>
        Run these in the vault’s folder. The wiki is created private and empty; the vault’s own wiki ID is
        offered to the server. On a second computer, run{' '}
        <code>
          athenaeum sync clone &lt;you&gt;/{repo} ./{repo} --server {server}
        </code>
        .
      </p>
    </div>
  );
}
